import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import type {
  Product,
  ProductFilters,
  ProductsResponse,
  ProductResponse,
  ProductSearchResponse,
} from "../types/product";

// ==========================================
// 📋 HOOKS PARA LISTA DE PRODUCTOS
// ==========================================

/**
 * Hook para obtener lista paginada de productos con filtros
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useProducts({
 *   categoryId: "123",
 *   active: true,
 *   outstanding: true,
 *   page: 1,
 *   limit: 10,
 * });
 * ```
 */
export function useProducts(filters?: ProductFilters) {
  return useQuery({
    queryKey: ["products", filters],
    queryFn: async (): Promise<ProductsResponse> => {
      const params = new URLSearchParams();

      if (filters?.categoryId) params.append("categoryId", filters.categoryId);
      if (filters?.active !== undefined) params.append("active", filters.active.toString());
      if (filters?.outstanding !== undefined) params.append("outstanding", filters.outstanding.toString());
      if (filters?.brand) params.append("brand", filters.brand);
      if (filters?.search) params.append("search", filters.search);
      if (filters?.includeDeleted) params.append("includeDeleted", filters.includeDeleted.toString());
      if (filters?.sortField) params.append("sortField", filters.sortField);
      if (filters?.sortDirection) params.append("sortDirection", filters.sortDirection);
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.limit) params.append("limit", filters.limit.toString());

      const response = await apiClient.get<ProductsResponse>(
        `/products?${params.toString()}`
      );
      return response;
    },
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos
  });
}

/**
 * Hook para obtener productos por ID de categoría (paginado)
 * 
 * @example
 * ```tsx
 * const { data } = useProductsByCategory("category-uuid", 1, 10);
 * ```
 */
export function useProductsByCategory(
  categoryId: string,
  page = 1,
  limit = 10,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["products", "category", categoryId, page, limit],
    queryFn: async (): Promise<ProductsResponse> => {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const url = `/product/category/${categoryId}?${params.toString()}`;

      const response = await apiClient.get<ProductsResponse>(url);
      
      // Normalizar la respuesta del backend
      // El backend envía "meta" pero normalizamos a "pagination" para consistencia
      const meta = response.meta || response.pagination;
      
      return {
        ...response,
        data: response.data || [],
        pagination: meta ? {
          total: meta.total ?? 0,
          page: meta.page ?? page,
          limit: meta.limit ?? limit,
          totalPages: meta.totalPages ?? Math.ceil((meta.total ?? 0) / (meta.limit ?? limit)),
          hasNext: meta.hasNext ?? false,
          hasPrev: meta.hasPrev ?? false,
        } : undefined,
      };
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!categoryId,
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos
  });
}

/**
 * Hook para obtener productos destacados
 * 
 * @example
 * ```tsx
 * const { data } = useOutstandingProducts(10);
 * // O filtrado por categoría:
 * const { data } = useOutstandingProducts(10, "category-uuid");
 * ```
 */
export function useOutstandingProducts(
  limit = 10,
  categoryId?: string
) {
  return useQuery({
    queryKey: ["products", "outstanding", limit, categoryId],
    queryFn: async (): Promise<{ success: boolean; data: Product[] }> => {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      if (categoryId) params.append("categoryId", categoryId);

      const response = await apiClient.get<{ success: boolean; data: Product[] }>(
        `/product/outstanding?${params.toString()}`
      );
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos (destacados cambian menos)
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

// ==========================================
// 🔍 HOOKS PARA BÚSQUEDA
// ==========================================

/**
 * Hook para búsqueda avanzada de productos
 * Incluye ranking, highlighting y paginación
 * 
 * @example
 * ```tsx
 * const { data } = useProductSearch({
 *   query: "cerámica",
 *   categoryId: "123",
 *   page: 1,
 *   limit: 20,
 * });
 * ```
 */
export function useProductSearchLegacy(params: {
  query: string;
  categoryId?: string;
  brand?: string;
  outstanding?: boolean;
  active?: boolean;
  includeDeleted?: boolean;
  ranking?: boolean;
  highlighting?: boolean;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["products", "search", params],
    queryFn: async (): Promise<ProductSearchResponse> => {
      const searchParams = new URLSearchParams();
      
      searchParams.append("query", params.query);
      if (params.categoryId) searchParams.append("categoryId", params.categoryId);
      if (params.brand) searchParams.append("brand", params.brand);
      if (params.outstanding !== undefined) searchParams.append("outstanding", params.outstanding.toString());
      if (params.active !== undefined) searchParams.append("active", params.active.toString());
      if (params.includeDeleted) searchParams.append("includeDeleted", params.includeDeleted.toString());
      if (params.ranking !== undefined) searchParams.append("ranking", params.ranking.toString());
      if (params.highlighting !== undefined) searchParams.append("highlighting", params.highlighting.toString());
      if (params.page) searchParams.append("page", params.page.toString());
      if (params.limit) searchParams.append("limit", params.limit.toString());

      const response = await apiClient.get<ProductSearchResponse>(
        `/product?${searchParams.toString()}`
      );
      return response;
    },
    enabled: !!params.query && params.query.length >= 2,
    staleTime: 1000 * 30, // 30 segundos (búsquedas son más volátiles)
    gcTime: 1000 * 60 * 2, // 2 minutos
  });
}

// ==========================================
// 📄 HOOKS PARA PRODUCTO INDIVIDUAL
// ==========================================

/**
 * Hook para obtener producto por ID
 * 
 * @example
 * ```tsx
 * const { data } = useProduct("product-uuid");
 * ```
 */
export function useProduct(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["product", id],
    queryFn: async (): Promise<Product> => {
      const response = await apiClient.get<ProductResponse>(`/product/${id}`);
      return response.data;
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

/**
 * Hook para obtener productos relacionados con un producto específico
 * Los productos relacionados son aquellos de la misma categoría, excluyendo el producto actual
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useRelatedProducts("product-uuid", 6);
 * ```
 */
export function useRelatedProducts(
  productId: string,
  limit: number = 6,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["products", "related", productId, limit],
    queryFn: async (): Promise<Product[]> => {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());

      const url = `/product/${productId}/related?${params.toString()}`;
      const response = await apiClient.get<{ success: boolean; data: Product[] }>(url);
      
      return response.data || [];
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!productId,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

/**
 * Hook para obtener producto por slug
 * 
 * @example
 * ```tsx
 * const { data } = useProductBySlug("ceramica-moderna");
 * ```
 */
export function useProductBySlug(
  slug: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["product", "by-slug", slug],
    queryFn: async (): Promise<Product> => {
      const response = await apiClient.get<ProductResponse>(
        `/product/slug/${slug}`
      );
      return response.data;
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

// ==========================================
// 🔄 HOOKS LEGACY (DEPRECADOS)
// ==========================================

/**
 * @deprecated Usar `useProductsByCategory()` en su lugar
 * 
 * Este hook usaba el endpoint `/category/by-slug/products/:slug`
 * que ya no existe. Ahora debes:
 * 1. Obtener la categoría por slug
 * 2. Usar su ID con `useProductsByCategory(categoryId)`
 * 
 * O pasar el categoryId via state al navegar.
 */
export function useProductsByCategorySlug(
  categorySlug: string,
  page = 1,
  limit = 10
) {
  return useQuery({
    queryKey: ["products", "by-category-slug-DEPRECATED", categorySlug, page, limit],
    queryFn: async (): Promise<ProductsResponse> => {
      throw new Error(
        "Endpoint /category/by-slug/products/:slug ya no existe. " +
        "Usa useProductsByCategory(categoryId) o pasa la categoría via state."
      );
    },
    enabled: false, // Deshabilitar este hook
  });
}
